import React, { FC, forwardRef, ReactNode, Ref, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Divider,
  Grid, IconButton,
  ListItemIcon, ListItemText,
  MenuItem,
  Stack, Tooltip,
  Typography,
  useMediaQuery
} from "@mui/material";
import PhotoPreview from "../PhotoPreview";
import Iconify from "../../../../../../../components/Iconify";
import { capitalizedString } from "../../../../../../../helpers/capitalizedString";
import { alpha, Theme } from "@mui/material/styles";
import { Chip } from "../index";
import { Gallery, Item } from "react-photoswipe-gallery";
import {
  DepartmentEnum,
  refetchGetSportEquipmentsQuery,
  SportEquipmentEntity,
  SportEquipmentTransferStatus,
  TechnicalCondition,
  useSportEquipmentWriteOffCancelMutation
} from "../../../../../../../graphql/graphQlApiHooks";
import { useTheme } from "@mui/styles";
import CardField from "./CardField";
import Settings from "../../../../../../../store/settings";
import { useLocation, useNavigate } from "react-router-dom";
import { SportEquipmentWriteOffDialogOpen } from "../../WriteOff/sport-equipment-write-off-dialog-state";
import {
  useGetSportEquipmentInputVars
} from "../../../../../../../store/reactiveVarsStores/spor-equipmnts/getSportEquipmentsInput";
import DownloadIcon from "@mui/icons-material/Download";
import { downloadFile } from "../../../../../../../helpers/downloadFile";
import { userVars } from "../../../../../../../store/reactiveVarsStores/auth/user";
import { useReactiveVar } from "@apollo/client";

interface Props {
  sportEquipment: SportEquipmentEntity;
  onClose: () => void;
}

const SportEquipmentCard: FC<Props> = ({ sportEquipment, onClose }: Props) => {
  const ref = useRef(null);
  const [isLocationCollapse, setIsLocationCollapse] = useState(false);

  useEffect(() => {

    const handleScroll = () => {
      setIsLocationCollapse(ref.current.scrollTop > 0);
    };

    ref?.current?.addEventListener("scroll", handleScroll);

    return () => {
      ref?.current?.removeEventListener("scroll", handleScroll);
    };

  }, [ref]);

  const navigate = useNavigate();


  const location = useLocation();
  
  const getEditPath = () => {
    if (location.pathname.startsWith('/repairs-request')) {
      return `/repairs-request/equipments/edit/${sportEquipment?.id}`;
    } else {
      return `/equipments/edit/${sportEquipment?.id}`;
    }
  };

  return (
    <Grid
      container
      rowSpacing={0}
      columnSpacing={0}
      sx={{ height: "100%" }}
    >
      <Grid
        item
        xs={12}
        sm={5}
        sx={{ 
          position: "relative",
          height: "100%",
          display: "flex",
          flexDirection: "column"
        }}
      >
        <PhotoPreview
          name={sportEquipment?.name ? sportEquipment.name : "Фото оборудования"}
          photos={sportEquipment?.photos}
          mainPhoto={sportEquipment?.mainPhoto}
        />
      </Grid>
      <Grid
        item
        xs={12}
        sm={7}
        sx={{ height: "100%" }}
      >
        <SportEquipmentCardContent ref={ref}>
          <SportEquipmentCardHeader
            sportEquipment={sportEquipment}
            isCollapse={isLocationCollapse}
          />
          <SportEquipmentCardMain sportEquipment={sportEquipment} />
          <SportEquipmentCardFooter
            handleEdit={() => navigate(getEditPath())}
            handleClose={() => {
              onClose()
            }}
            sportEquipment={sportEquipment}
          />
        </SportEquipmentCardContent>

      </Grid>
    </Grid>
  );
};

export default SportEquipmentCard;


export const SportEquipmentCardContent = forwardRef(({ children }: { children: ReactNode }, ref: Ref<HTMLDivElement>) => {
  const theme = useTheme();

  return (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        position: "relative",
        bgcolor: "background.paper",
        borderRadius: 2,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden"
      }}
    >
      <Box
        ref={ref}
        sx={{
          flex: 1,
          overflow: "auto",
          overflowX: "hidden",
          position: "relative",
          "&::-webkit-scrollbar": {
            width: "8px"
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent"
          },
          "&::-webkit-scrollbar-thumb": {
            background: alpha(theme.palette.divider, 0.3),
            borderRadius: "4px",
            "&:hover": {
              background: alpha(theme.palette.divider, 0.5)
            }
          }
        }}
      >
        {children}
      </Box>
    </Card>
  );
});
export const SportEquipmentCardHeader = ({
                                    sportEquipment,
                                    isCollapse
                                  }: { sportEquipment: SportEquipmentEntity, isCollapse: boolean }) => {
  const theme = useTheme();
  const sm = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));

  const getTransferStatusLabel = (status: SportEquipmentTransferStatus | null | undefined): string => {
    if (!status) return "Статус передачи не указан";
    
    switch (status) {
      case SportEquipmentTransferStatus.TransferredToSubordinate:
        return "Передано подведу";
      default:
        return "Неизвестный статус";
    }
  };
  
  return (
    <>
      {/* Location Info - Metadata */}
      <Box sx={{ pt: 3, px: { xs: 3, sm: 4 } }}>
        <Stack
          direction={"row"}
          alignItems="center"
          spacing={1}
          sx={{
            mb: 3
          }}
        >
          <Iconify
            icon={"mdi:map-marker"}
            color={"text.secondary"}
            fontSize={"1rem"}
            sx={{ flexShrink: 0 }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant={"caption"}
              color={"text.secondary"}
              sx={{
                display: "block",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                lineHeight: 1.5,
                fontSize: "0.75rem"
              }}
            >
              {capitalizedString(sportEquipment?.sportSubZone?.name) || "Подзона не указана"}
              {sportEquipment?.sportObject?.name && ` • ${capitalizedString(sportEquipment?.sportObject?.name)}`}
              {sportEquipment?.sportZone?.name && ` • ${capitalizedString(sportEquipment?.sportZone?.name)}`}
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* Title - Sticky */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          bgcolor: alpha(theme.palette.background.paper, 0.98),
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
          pt: isCollapse ? 0.5 : 1,
          pb: isCollapse ? 0.75 : 2,
          mb: isCollapse ? 1 : 2,
          px: { xs: 3, sm: 4 },
          transition: "all 0.15s ease-out",
          boxShadow: isCollapse ? `0 2px 8px ${alpha(theme.palette.common.black, 0.05)}` : "none"
        }}
      >
        <Typography
          variant={"h3"}
          color={"text.primary"}
          sx={{
            fontWeight: 700,
            lineHeight: isCollapse ? 1.1 : 1.2,
            display: "flex",
            alignItems: "center",
            gap: 1,
            fontSize: { xs: "1.75rem", sm: "2rem" },
            transform: isCollapse ? "scale(0.7)" : "scale(1)",
            transformOrigin: "left center",
            transition: "transform 0.15s ease-out"
          }}
        >
          {sportEquipment?.name}
          {sportEquipment?.technicalCondition === TechnicalCondition.NotWorking && (
            <Iconify
              icon={"mdi:progress-wrench"}
              color={"error.main"}
              fontSize={"1.75rem"}
              sx={{
                transform: isCollapse ? "scale(0.7)" : "scale(1)",
                transition: "transform 0.15s ease-out"
              }}
            />
          )}
        </Typography>
      </Box>

      {/* Status Chips */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 3, px: { xs: 3, sm: 4 } }}>
        <Chip
          state={sportEquipment?.technicalCondition === TechnicalCondition.Working}
          type={"technicalCondition"}
          size={"small"}
          icon={<Iconify
            icon={"bi:circle-fill"}
            color={"inherit"}
            fontSize={"inherit"}
          />}
          label={sportEquipment?.technicalCondition === TechnicalCondition.Working ? "Исправно" : "Не исправно"}
        />

        <Chip
          state={sportEquipment?.inUse}
          type={"inUse"}
          size={"small"}
          icon={<Iconify
            icon={"bi:circle-fill"}
            color={"inherit"}
            fontSize={"inherit"}
          />}
          label={sportEquipment?.inUse ? "Используется" : "Не используется"}
        />

        {sportEquipment?.transferStatus && (
          <Chip
            state={sportEquipment?.transferStatus === SportEquipmentTransferStatus.TransferredToSubordinate}
            type={"transferStatus"}
            size={"small"}
            icon={<Iconify
              icon={"bi:circle-fill"}
              color={"inherit"}
              fontSize={"inherit"}
            />}
            label={getTransferStatusLabel(sportEquipment?.transferStatus)}
          />
        )}
      </Box>

      {/* Write-off Alert */}
      {sportEquipment.isWroteOff && (
        <Box sx={{ px: { xs: 3, sm: 4 } }}>
          <Alert
            severity={"error"}
            sx={{
              mb: 3,
              borderRadius: 2,
              "& .MuiAlert-icon": {
                alignItems: "center"
              }
            }}
            action={
              sportEquipment.wroteOffCertificate && (
                <Tooltip title='Скачать'>
                  <IconButton
                    size="small"
                    onClick={() => downloadFile(sportEquipment.wroteOffCertificate)}
                    sx={{ color: "inherit" }}
                  >
                    <DownloadIcon fontSize='small' />
                  </IconButton>
                </Tooltip>
              )
            }
          >
            <Typography variant={"body2"} sx={{ fontWeight: 600, mb: 0.5 }}>
              Оборудование списано
            </Typography>
            <Typography variant={"caption"} color={"text.secondary"}>
              {sportEquipment.wroteOffCertificate?.name || "Акт о списании не найден"}
            </Typography>
          </Alert>
        </Box>
      )}

      {/* Failure Reason Alert */}
      {sportEquipment?.failureReason && (
        <Box sx={{ px: { xs: 3, sm: 4 } }}>
          <Alert
            severity={"warning"}
            sx={{
              mb: 3,
              borderRadius: 2
            }}
          >
            <Typography variant={"body2"} sx={{ fontWeight: 600, mb: 0.5 }}>
              Причина неисправности
            </Typography>
            <Typography variant={"caption"} color={"text.secondary"}>
              {sportEquipment?.failureReason}
            </Typography>
          </Alert>
        </Box>
      )}

      {/* Quick Info */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 2,
          p: 2.5,
          px: { xs: 3, sm: 4 },
          bgcolor: alpha(theme.palette.primary.main, 0.04),
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          mb: 3,
          mx: { xs: 3, sm: 4 }
        }}
      >
        <CardField
          label={"Бухгалтерское название"}
          value={sportEquipment?.accountingName ? sportEquipment?.accountingName : sportEquipment?.name}
          vertical
          ValueProps={{
            variant: "body2",
            sx: {
              fontWeight: 500
            }
          }}
        />

        <CardField
          label={"Инвентарный номер"}
          value={sportEquipment?.inventoryNumber || "Не указан"}
          vertical
          ValueProps={{
            variant: "body2",
            sx: {
              color: "primary.main",
              fontWeight: 600
            }
          }}
        />

        {/* Barcode Photo Field */}
        {(sportEquipment as any)?.barcodePhoto && (sportEquipment as any).barcodePhoto.url && (
          <Gallery>
            <CardField
              label={"Штрих-код оборудования"}
              value={
                <Item
                  original={
                    (sportEquipment as any).barcodePhoto.url?.includes("http")
                      ? (sportEquipment as any).barcodePhoto.url
                      : Settings.server + (sportEquipment as any).barcodePhoto.url
                  }
                  thumbnail={
                    (sportEquipment as any).barcodePhoto.url?.includes("http")
                      ? (sportEquipment as any).barcodePhoto.url
                      : Settings.server + ((sportEquipment as any).barcodePhoto.avatarUrl || (sportEquipment as any).barcodePhoto.url)
                  }
                  width={(sportEquipment as any).barcodePhoto.width || 800}
                  height={(sportEquipment as any).barcodePhoto.height || 600}
                  alt="Штрих-код"
                >
                  {({ ref, open }) => (
                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: 1,
                        overflow: "hidden",
                        border: "1px solid",
                        borderColor: "divider",
                        flexShrink: 0,
                        cursor: "pointer"
                      }}
                      onClick={open}
                      ref={ref as React.MutableRefObject<HTMLDivElement>}
                    >
                      <img
                        src={
                          (sportEquipment as any).barcodePhoto.url?.includes("http")
                            ? (sportEquipment as any).barcodePhoto.url
                            : Settings.server + ((sportEquipment as any).barcodePhoto.avatarUrl || (sportEquipment as any).barcodePhoto.url)
                        }
                        alt="Штрих-код"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover"
                        }}
                      />
                    </Box>
                  )}
                </Item>
              }
              vertical
              ValueProps={{
                variant: "body2"
              }}
            />
          </Gallery>
        )}
      </Box>
    </>
  );
};
export const SportEquipmentCardMain = ({ sportEquipment }: { sportEquipment: SportEquipmentEntity }) => {
  const theme = useTheme();
  const description = sportEquipment?.description || sportEquipment?.specifications?.[0]?.valueString || "Описание отсутствует";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
        pb: 3
      }}
    >
      {/* Description Section */}
      {description && description !== "Описание отсутствует" && (
        <Box
          sx={{
            p: 3,
            px: { xs: 3, sm: 4 },
            mx: { xs: 3, sm: 4 },
            bgcolor: alpha(theme.palette.primary.main, 0.04),
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
          }}
        >
          <Typography
            variant={"subtitle2"}
            sx={{
              fontWeight: 600,
              mb: 1.5,
              color: "text.primary",
              display: "flex",
              alignItems: "center",
              gap: 1
            }}
          >
            <Iconify icon={"mdi:text-box-outline"} fontSize={"1.2rem"} />
            Описание
          </Typography>
          <Typography
            variant={"body2"}
            sx={{
              color: "text.secondary",
              lineHeight: 1.7
            }}
          >
            {description}
          </Typography>
        </Box>
      )}

      {/* Equipment Info Section */}
      <Box
        sx={{
          p: 3,
          px: { xs: 3, sm: 4 },
          mx: { xs: 3, sm: 4 },
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          bgcolor: "background.paper"
        }}
      >
        <Typography
          variant={"subtitle2"}
          sx={{
            fontWeight: 600,
            mb: 2.5,
            color: "text.primary",
            display: "flex",
            alignItems: "center",
            gap: 1
          }}
        >
          <Iconify icon={"mdi:information-outline"} fontSize={"1.2rem"} />
          Информация
        </Typography>

        <Stack spacing={2}>
          <CardField
            label={"Информация заполнена"}
            value={sportEquipment?.isInformationComplete ? "Да" : "Нет"}
            fullWidth
          />

          <CardField
            label={"Доступно для передачи"}
            value={sportEquipment?.availableForTransfer ? "Да" : "Нет"}
            fullWidth
          />

          <CardField
            label={"Дата следующего тех. обслуживания"}
            value={sportEquipment?.equipmentServicePeriods?.length ? sportEquipment?.equipmentServicePeriods[0]?.nextDate : "Не указана"}
            fullWidth
          />
        </Stack>
      </Box>

      {/* Characteristics Section */}
      <Box
        sx={{
          p: 3,
          px: { xs: 3, sm: 4 },
          mx: { xs: 3, sm: 4 },
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          bgcolor: "background.paper"
        }}
      >
        <Typography
          variant={"subtitle2"}
          sx={{
            fontWeight: 600,
            mb: 2.5,
            color: "text.primary",
            display: "flex",
            alignItems: "center",
            gap: 1
          }}
        >
          <Iconify icon={"mdi:format-list-bulleted"} fontSize={"1.2rem"} />
          Характеристики
        </Typography>

        <Stack spacing={2}>
          <CardField
            label={"Категория спорта"}
            value={sportEquipment?.sportCategoryObject?.name || "Не указана"}
            fullWidth
          />

          <CardField
            label={"Вид спорта"}
            value={sportEquipment?.sportKindObject?.name || "Не указан"}
            fullWidth
          />

          <CardField
            label={"Тип оборудования"}
            value={sportEquipment?.equipmentTypeObject?.name || "Не указан"}
            fullWidth
          />

          <CardField
            label={"Длина"}
            value={sportEquipment?.length ? `${sportEquipment.length} см` : "Не указана"}
            fullWidth
          />

          <CardField
            label={"Ширина"}
            value={sportEquipment?.width ? `${sportEquipment.width} см` : "Не указана"}
            fullWidth
          />

          <CardField
            label={"Высота"}
            value={sportEquipment?.height ? `${sportEquipment.height} см` : "Не указана"}
            fullWidth
          />

          <CardField
            label={"Вес"}
            value={sportEquipment?.weight ? `${sportEquipment.weight} кг` : "Не указан"}
            fullWidth
          />
        </Stack>
      </Box>

      {/* Documents Section */}
      <Box
        sx={{
          p: 3,
          px: { xs: 3, sm: 4 },
          mx: { xs: 3, sm: 4 },
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          bgcolor: "background.paper"
        }}
      >
        <Typography
          variant={"subtitle2"}
          sx={{
            fontWeight: 600,
            mb: 2.5,
            color: "text.primary",
            display: "flex",
            alignItems: "center",
            gap: 1
          }}
        >
          <Iconify icon={"mdi:file-document-outline"} fontSize={"1.2rem"} />
          Документы
        </Typography>

        <Stack spacing={1.5}>
          <CardField
            label={"Акт ввода в эксплуатацию"}
            value={
              <MenuItem
                component={"a"}
                title={sportEquipment?.guarantee?.[0]?.description}
                href={Settings.server + sportEquipment?.guarantee?.[0]?.url}
                disabled={!sportEquipment?.guarantee?.[0]}
                target={"_blank"}
                disableGutters={true}
                sx={{
                  p: 0,
                  minHeight: "auto",
                  borderRadius: 1,
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.08)
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Iconify icon={"material-symbols:sim-card-download-outline"} />
                </ListItemIcon>
                <ListItemText 
                  primary="Скачать"
                  primaryTypographyProps={{
                    variant: "body2",
                    sx: { fontWeight: 500 }
                  }}
                />
              </MenuItem>
            }
            fullWidth
          />

          <CardField
            label={"Паспорт"}
            value={
              <MenuItem
                component={"a"}
                title={sportEquipment?.passport?.[0]?.description}
                href={Settings.server + sportEquipment?.passport?.[0]?.url}
                disabled={!sportEquipment?.passport?.[0]}
                target={"_blank"}
                disableGutters={true}
                sx={{
                  p: 0,
                  minHeight: "auto",
                  borderRadius: 1,
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.08)
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Iconify icon={"material-symbols:sim-card-download-outline"} />
                </ListItemIcon>
                <ListItemText 
                  primary="Скачать"
                  primaryTypographyProps={{
                    variant: "body2",
                    sx: { fontWeight: 500 }
                  }}
                />
              </MenuItem>
            }
            fullWidth
          />

          <CardField
            label={"Сертификат"}
            value={
              <MenuItem
                component={"a"}
                title={sportEquipment?.certificate?.[0]?.description}
                href={Settings.server + sportEquipment?.certificate?.[0]?.url}
                disabled={!sportEquipment?.certificate?.[0]}
                target={"_blank"}
                disableGutters={true}
                sx={{
                  p: 0,
                  minHeight: "auto",
                  borderRadius: 1,
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.08)
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Iconify icon={"material-symbols:sim-card-download-outline"} />
                </ListItemIcon>
                <ListItemText 
                  primary="Скачать"
                  primaryTypographyProps={{
                    variant: "body2",
                    sx: { fontWeight: 500 }
                  }}
                />
              </MenuItem>
            }
            fullWidth
          />

          <CardField
            label={"Гарантийный талон"}
            value={
              <MenuItem
                component={"a"}
                title={sportEquipment?.guarantee?.[0]?.description}
                href={Settings.server + sportEquipment?.guarantee?.[0]?.url}
                disabled={!sportEquipment?.guarantee?.[0]}
                target={"_blank"}
                disableGutters={true}
                sx={{
                  p: 0,
                  minHeight: "auto",
                  borderRadius: 1,
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.08)
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Iconify icon={"material-symbols:sim-card-download-outline"} />
                </ListItemIcon>
                <ListItemText 
                  primary="Скачать"
                  primaryTypographyProps={{
                    variant: "body2",
                    sx: { fontWeight: 500 }
                  }}
                />
              </MenuItem>
            }
            fullWidth
          />

          <CardField
            label={"Руководство по эксплуатации"}
            value={
              <MenuItem
                component={"a"}
                title={sportEquipment?.manual?.[0]?.description}
                href={Settings.server + sportEquipment?.manual?.[0]?.url}
                disabled={!sportEquipment?.manual?.[0]}
                target={"_blank"}
                disableGutters={true}
                sx={{
                  p: 0,
                  minHeight: "auto",
                  borderRadius: 1,
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.08)
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Iconify icon={"material-symbols:sim-card-download-outline"} />
                </ListItemIcon>
                <ListItemText 
                  primary="Скачать"
                  primaryTypographyProps={{
                    variant: "body2",
                    sx: { fontWeight: 500 }
                  }}
                />
              </MenuItem>
            }
            fullWidth
          />

          {sportEquipment?.otherDocuments?.length > 0 && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography 
                variant={"caption"} 
                color={"text.secondary"} 
                sx={{ 
                  px: 1.5,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: 0.5
                }}
              >
                Прочие документы
              </Typography>
              {sportEquipment?.otherDocuments.map((document: any, i: number) => (
                <MenuItem
                  key={i}
                  component={"a"}
                  title={document.description}
                  href={Settings.server + document.url}
                  target={"_blank"}
                  dense
                  sx={{
                    borderRadius: 1,
                    "&:hover": {
                      bgcolor: alpha(theme.palette.primary.main, 0.08)
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Iconify icon={"material-symbols:sim-card-download-outline"} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={document.description}
                    primaryTypographyProps={{
                      variant: "body2"
                    }}
                  />
                </MenuItem>
              ))}
            </>
          )}
        </Stack>
      </Box>

    </Box>
  );
};

export const SportEquipmentCardFooter = ({
  handleEdit,
  handleClose,
  sportEquipment
}: {
  handleEdit: () => void,
  handleClose: () => void,
  sportEquipment: SportEquipmentEntity
}) => {
  const theme = useTheme();
  const [writeOffCancelMutation] = useSportEquipmentWriteOffCancelMutation();
  const { getInput } = useGetSportEquipmentInputVars();
  const user = useReactiveVar(userVars);

  const handleWriteOff = async () => {
    if (!sportEquipment.isWroteOff) {
      SportEquipmentWriteOffDialogOpen(sportEquipment?.id, handleClose);
    } else {
      await writeOffCancelMutation({
        variables: { sportEquipmentId: sportEquipment.id },
        refetchQueries: [
          refetchGetSportEquipmentsQuery({ input: getInput() })
        ],
      });
      handleClose();
    }
  }

  return (
    <Box
      sx={{
        position: "sticky",
        bottom: 0,
        left: 0,
        zIndex: 1,
        width: "100%",
        pt: 1,
        pb: 0,
        px: 0,
        borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
        backdropFilter: "blur(10px)",
        bgcolor: alpha(theme.palette.background.paper, 0.95)
      }}
    >
      <Stack
        direction="row"
        spacing={1.5}
        justifyContent="flex-end"
        alignItems="center"
        sx={{ pb: 1.5, px: { xs: 3, sm: 4 } }}
      >
        <Button
          variant='outlined'
          color='inherit'
          size={"small"}
          onClick={handleEdit}
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 500
          }}
        >
          Изменить
        </Button>

        {user.departments.includes(DepartmentEnum.EquipmentAcceptanceAndDiagnostics) && (
          <Button
            variant='outlined'
            color={sportEquipment.isWroteOff ? "success" : "error"}
            size={"small"}
            onClick={handleWriteOff}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 500
            }}
          >
            {sportEquipment.isWroteOff ? "Вернуть со списания" : "Списать"}
          </Button>
        )}

        <Button
          variant='contained'
          color='primary'
          size={"small"}
          onClick={handleClose}
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 500,
            px: 2.5
          }}
        >
          Закрыть
        </Button>
      </Stack>
    </Box>
  );
};
